<template>
    <switch-theme-message />

    <LaPageHeader heading="Dashboard" data-cy="yield-dashboard">
        <template #controls>
            <!-- Slots (single publisher mode only) -->
            <LaBaseSelect
                v-if="isSinglePublisherMode && availableSlotNames.length"
                class="mr-4"
                data-test="slots-select"
                :items="availableSlotNames"
                v-model="availableSlotNamesSelected"
                itemValue="name"
                itemText="name"
                itemLabel="slots"
                :multiple="true"
                @change="onChangeAvailableSlotNames"
            />

            <!-- Websites (single publisher mode only) -->
            <LaWebsiteDatePickers
                v-if="isSinglePublisherMode && websites?.length"
                v-model="websiteDatesModel"
                :websites="websites"
                :tz="tz"
                :multiple="true"
                :select-all-defaults="true"
            />

            <!-- Period (multi-publisher mode only) -->
            <LaMultiPublisherDatePicker v-if="!isSinglePublisherMode" />
        </template>
    </LaPageHeader>

    <portal-target name="dashboardTop" v-if="selectedPublishers?.length" />

    <LaNoData
        v-if="!selectedPublishers?.length"
        header="Select publishers"
        description="Please select at least one publisher to show yield data."
    />

    <!-- Single publisher mode -->
    <template v-if="isSinglePublisherMode">
        <!-- Show loading while websites are being loaded. Mirrors the YieldCards
             layout below: three rows of 3/2/4 stat cards plus the chart+table card. -->
        <template v-if="!websitesReady">
            <div class="columns" v-for="count in [3, 2, 4]" :key="count">
                <div class="column" v-for="index in count" :key="index">
                    <LaCardLoader class="small-card" />
                </div>
            </div>
            <div class="columns">
                <div class="column">
                    <LaDataCardLoader card />
                </div>
            </div>
        </template>
        <!-- Show YieldCards only when websites are ready -->
        <LaYieldCards
            v-else
            :payload="singlePublisherFilterData"
            :websites="selectedWebsites"
            @update-available-slot-names="selectSlotNames"
        />
    </template>

    <!-- Multi-publisher mode -->
    <template v-else-if="selectedPublishers.length > 1">
        <LaMultiPublisherYieldCards :payload="multiPublisherFilterData" :publishers="selectedPublishers" />
    </template>
</template>

<script>
import LaPageHeader from "@/components/PageHeader";
import store from "@/store/store";
import LaNoData from "@/components/NoData";
import LaCardLoader from "@/components/loaders/CardLoader";
import LaDataCardLoader from "@/components/loaders/DataCardLoader";
import LaWebsiteDatePickers from "@/components/filters/WebsiteDatePickers";
import LaYieldCards from "@/components/yield/YieldCards";
import LaMultiPublisherDatePicker from "@/components/multiPublisher/MultiPublisherDatePicker.vue";
import LaMultiPublisherYieldCards from "@/components/multiPublisher/MultiPublisherYieldCards.vue";
import { actionTypes as websiteActionTypes, getterTypes as websiteGetterTypes } from "@/store/Websites/types";
import { getterTypes as userGetterTypes } from "@/store/LoggedUser/types";
import { actionTypes as publisherActionTypes, getterTypes as publisherGetterTypes } from "@/store/Publisher/types";
import {
    actionTypes as websiteDatesActionTypes,
    getterTypes as websiteDatesGetterTypes,
} from "@/store/WebsiteDates/types";
import { computed, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { getDefaultPeriod, getValidAabSiteIds, isWebsiteMeasured } from "@/helpers/data";
import { useDashboardState } from "@/composables/useDashboardState";
import SwitchThemeMessage from "@/components/SwitchThemeMessage.vue";

import LaBaseSelect from "@/components/inputs/BaseSelect.vue";

export default {
    name: "DashboardYield",
    components: {
        LaBaseSelect,
        SwitchThemeMessage,
        LaPageHeader,
        LaCardLoader,
        LaDataCardLoader,
        LaWebsiteDatePickers,
        LaNoData,
        LaYieldCards,
        LaMultiPublisherDatePicker,
        LaMultiPublisherYieldCards,
    },
    props: {
        from_database: {
            type: String,
            default: "sql",
        },
    },
    methods: {
        onChangeAvailableSlotNames() {
            // Get namespace from composable
            const { namespace } = useDashboardState("yield", this.$route);
            store.dispatch(websiteDatesActionTypes.setAvailableSlotNamesToLS, {
                availableSlotNames: this.availableSlotNamesSelected,
                namespace: namespace,
            });
        },
        selectSlotNames(newSlotNames) {
            const items = newSlotNames.map((slot) => ({ name: slot }));
            const validNames = new Set(items.map((i) => i.name));
            const current = this.availableSlotNamesSelected || [];
            const kept = current.filter((s) => validNames.has(s.name));
            const target = kept.length ? kept : items;
            // Only assign when names actually differ — assigning a new array
            // reference with the same content still triggers reactivity, which
            // recomputes singlePublisherFilterData → refetches → re-emits →
            // re-enters selectSlotNames, producing a request loop.
            const targetNames = target.map((t) => t.name);
            const currentNames = current.map((s) => s.name);
            const same =
                targetNames.length === currentNames.length && targetNames.every((n, i) => n === currentNames[i]);
            if (!same) {
                this.availableSlotNamesSelected = target;
            }
            // Items list can be reassigned freely — it doesn't feed the API
            // payload, only the dropdown options. But still skip if equal to
            // avoid pointless BaseSelect re-renders.
            const currentItemNames = (this.availableSlotNames || []).map((s) => s.name);
            const sameItems =
                targetNames.length === currentItemNames.length &&
                targetNames.every((n, i) => n === currentItemNames[i]);
            if (!sameItems) {
                this.availableSlotNames = items;
            }
        },
    },
    setup(props) {
        const router = useRouter();
        const route = useRoute();

        const userId = computed(() => store.getters[userGetterTypes.getUserId]);
        const tz = computed(() => store.getters[userGetterTypes.getTimezone]);
        const aabSiteIds = computed(() => store.getters[websiteDatesGetterTypes.getAabSiteIds]);
        const period = computed(() => store.getters[websiteDatesGetterTypes.getPeriod]);
        let availableSlotNames = ref([]);
        let availableSlotNamesSelected = ref([]);
        let websitesReady = ref(false);

        // Use the dashboard state composable
        const { setupRouteWatcher, setupUnmountCleanup, loadAndSetWebsitesForPublisher, namespace } = useDashboardState(
            "yield",
            route
        );

        // Set selected mode
        store.dispatch(publisherActionTypes.setSelectedMode, "yield");

        // Yield-specific state
        const yieldPublishers = computed(() => {
            const allPublishers = store.getters[publisherGetterTypes.getPublishers] || [];
            return allPublishers.filter((p) => p.is_yield === true);
        });

        const selectedPublishers = computed(() => store.getters[publisherGetterTypes.getSelectedPublishersYield]);
        const selectedWebsites = computed(() => store.getters[publisherGetterTypes.getSelectedWebsitesYield]);

        // Setup route watcher using composable
        setupRouteWatcher(async (_, newName) => {
            if (newName === "dashboard-yield") {
                // Clear legacy LTBA website-dates LS (prevents mixing across dashboards).
                localStorage.removeItem("lawebsitedates");
            }
        });

        // Initialize publishers if not loaded
        if (!yieldPublishers.value || yieldPublishers.value.length === 0) {
            store.dispatch(publisherActionTypes.getData);
        }

        // Helper for single vs multi publisher mode
        const isSinglePublisherMode = computed(() => selectedPublishers.value?.length === 1);

        // Load Yield websites — delegates to the shared composable. Passing
        // publisherId=null skips the LTBA publisher_id filter; Yield uses the
        // full Websites store result.
        const loadAndSetWebsites = () => loadAndSetWebsitesForPublisher(null, userId.value, period.value);

        // Websites (for single publisher mode)
        const websites = computed(
            () =>
                store.getters[websiteGetterTypes.getWebsites]?.map((web) => ({
                    ...web,
                    disabled: !isWebsiteMeasured(web),
                })) || null
        );

        const onChangeWebsiteDates = (val) => {
            if (!val.period || !val.websites || !userId.value) {
                return;
            }

            const validWebsiteIds = getValidAabSiteIds(val.websites);

            // Reset slot filter when the website set actually changes — slots are
            // per-website data and a stale selection would silently filter the
            // response down to (often empty) data, leaving the dropdown stuck on
            // the previous web's selection. Reset must happen synchronously
            // before setSelectedWebsitesYield so the next fetchData fires with
            // empty `slots`.
            const oldIdsKey = (getValidAabSiteIds(selectedWebsites.value || []) || []).slice().sort().join(",");
            const newIdsKey = validWebsiteIds.slice().sort().join(",");
            if (oldIdsKey !== newIdsKey) {
                availableSlotNamesSelected.value = [];
                availableSlotNames.value = [];
                store.dispatch(websiteDatesActionTypes.setAvailableSlotNamesToLS, {
                    availableSlotNames: [],
                    namespace: namespace,
                });
            }

            // Store in WebsiteDates store with yield namespace
            store.dispatch(websiteDatesActionTypes.setWebsiteDatesToLS, {
                aabSiteIds: validWebsiteIds,
                period: val.period,
                userId: userId.value,
                namespace: namespace,
            });
            // Also store in Yield store for state management
            store.dispatch(publisherActionTypes.setSelectedWebsitesYield, val.websites);
        };

        // Filter data for single publisher mode
        const singlePublisherFilterData = computed(() => {
            const selectedSlotNames =
                availableSlotNamesSelected.value != null
                    ? availableSlotNamesSelected.value.map((item) => item.name)
                    : [];
            // The API treats `slots: []` as "filter by nothing" (returns zeros) and
            // filtering by every available slot equals no filter. Send no filter in
            // both cases so the first fetch already returns real data and selecting
            // all slots afterwards doesn't change the payload (no second fetch).
            const availableNames = (availableSlotNames.value || []).map((item) => item.name);
            const allSelected =
                selectedSlotNames.length > 0 &&
                selectedSlotNames.length === availableNames.length &&
                selectedSlotNames.every((name) => availableNames.includes(name));
            const slots = selectedSlotNames.length === 0 || allSelected ? null : selectedSlotNames;

            // Pro Yield dashboard - vždy použij všechny dostupné websites když nejsou vybrané konkrétní
            let websiteIds = [];

            // Priorita 1: Použij vybrané websites
            if (selectedWebsites.value && selectedWebsites.value.length > 0) {
                websiteIds = getValidAabSiteIds(selectedWebsites.value);
            } else if (aabSiteIds.value && aabSiteIds.value.length > 0) {
                // Priorita 2: Použij websites ze store (z localStorage)
                websiteIds = [...aabSiteIds.value];
            } else if (websites.value && websites.value.length > 0) {
                // Priorita 3: Použij všechny dostupné websites
                websiteIds = getValidAabSiteIds(websites.value);
            }

            return {
                period: period.value || getDefaultPeriod(),
                aabSiteIds: websiteIds,
                timezone: tz.value,
                slots,
                source: props.from_database,
            };
        });

        // Filter data for multi-publisher mode
        const multiPublisherFilterData = computed(() => ({
            period: period.value || getDefaultPeriod(),
            timezone: tz.value,
        }));

        const websiteDatesModel = computed({
            get: () => ({
                websites: selectedWebsites.value,
                period: period.value || getDefaultPeriod(),
            }),
            set: onChangeWebsiteDates,
        });

        // Restore from localStorage on mount
        store.dispatch(publisherActionTypes.restoreFromLS);

        // Synchronously populate WebsiteDates.period from LS so the UI shows the saved
        // period immediately, before any async load/watch can fall through to defaults.
        if (userId.value) {
            store.dispatch(websiteDatesActionTypes.getWebsiteDatesfromLS, {
                userId: userId.value,
                namespace: namespace,
            });
        }

        // Auto-select all publishers on first render if nothing is selected (no LS restore).
        watch(
            [yieldPublishers, selectedPublishers],
            ([publishers, selected]) => {
                if (publishers.length > 0 && (!selected || selected.length === 0)) {
                    store.dispatch(publisherActionTypes.setSelectedPublishersYield, [...publishers]);
                }
            },
            { immediate: true }
        );

        // Clear selected websites only when the user actually leaves single mode
        // (single → multi). Going multi → single happens during mount as
        // restoreFromLS populates publishers from [] to [pub], and resetting here
        // would wipe the LS-saved subset before loadAndSetWebsites can preserve it.
        watch(isSinglePublisherMode, (newVal, oldVal) => {
            if (oldVal === true && newVal === false) {
                store.dispatch(publisherActionTypes.setSelectedWebsitesYield, []);
            }
        });

        // Sync store FROM URL params (deep link / hard reload / back-forward). Watch a
        // function source — Vue Router mutates params on the existing route object, so
        // `watch(route, ...)` misses SPA param changes.
        //
        // When the URL has no :publisher param we intentionally do NOT touch the store:
        // leaving the LS-restored selection alone is what lets the user come back from
        // e.g. /fees and still see their previously chosen publisher. The URL→params
        // mirror is driven by the separate [selectedPublishers] watcher below, which
        // will promote the store selection into the URL.
        watch(
            () => [route.params.publisher, yieldPublishers.value],
            ([publisherParam, publishers]) => {
                if (route.name !== "dashboard-yield" || !publishers || publishers.length === 0) return;
                if (!publisherParam) return;

                const publisherIds = String(publisherParam)
                    .split(",")
                    .map((id) => parseInt(id.trim()))
                    .filter((id) => !isNaN(id));
                const foundPublishers = publishers.filter((p) => publisherIds.includes(p.id));
                if (!foundPublishers.length) return;

                const currentIds = selectedPublishers.value.map((p) => p.id).sort();
                const newIds = foundPublishers.map((p) => p.id).sort();
                const needsUpdate =
                    currentIds.length !== newIds.length || !currentIds.every((id, i) => id === newIds[i]);
                if (needsUpdate) {
                    store.dispatch(publisherActionTypes.setSelectedPublishersYield, foundPublishers);
                }
            },
            { immediate: true }
        );

        // Load websites whenever the single selected publisher ID changes. Keyed on ID
        // (not object reference) so it survives restore-from-LS cycles.
        watch(
            () => (isSinglePublisherMode.value ? selectedPublishers.value?.[0]?.id : null),
            (publisherId, oldPublisherId) => {
                // Reset slot filter on real publisher switch — old slots don't exist
                // for the new publisher and would filter the response down to empty
                // data, leaving the dropdown stuck on stale options.
                if (oldPublisherId !== undefined && publisherId !== oldPublisherId) {
                    availableSlotNamesSelected.value = [];
                    availableSlotNames.value = [];
                    store.dispatch(websiteDatesActionTypes.setAvailableSlotNamesToLS, {
                        availableSlotNames: [],
                        namespace: namespace,
                    });
                }
                if (!publisherId) {
                    websitesReady.value = true;
                    return;
                }
                websitesReady.value = false;
                loadAndSetWebsites()
                    .then(() => {
                        websitesReady.value = true;
                    })
                    .catch(() => {
                        websitesReady.value = true;
                    });
            },
            { immediate: true }
        );

        // Update URL when selection changes
        watch([selectedPublishers, selectedWebsites], ([publishers]) => {
            if (route && route.name === "dashboard-yield" && publishers) {
                let newParams = {};

                if (publishers.length === 0 || publishers.length === yieldPublishers.value.length) {
                    // No publishers or all publishers selected - no params needed
                    router.replace({ name: "dashboard-yield" }).catch(() => {
                        // Ignore navigation duplicated error
                    });
                } else if (publishers.length === 1) {
                    // Single publisher mode - don't include webs in URL
                    newParams.publisher = publishers[0].id.toString();
                    router.replace({ name: "dashboard-yield", params: newParams }).catch(() => {
                        // Ignore navigation duplicated error
                    });
                } else {
                    // Multiple publishers selected - comma separated IDs
                    newParams.publisher = publishers.map((p) => p.id).join(",");
                    router.replace({ name: "dashboard-yield", params: newParams }).catch(() => {
                        // Ignore navigation duplicated error
                    });
                }
            }
        });

        // Load websites and user data (for single publisher mode)
        watch(
            [websites, userId, isSinglePublisherMode],
            ([websitesVal, userIdVal, isSingleMode]) => {
                if (!isSingleMode) return;
                if (!websitesVal || !userIdVal) {
                    !websitesVal && store.dispatch(websiteActionTypes.getData);
                    return;
                }
                // Load period from yield namespace LS if Vuex period is missing.
                if (!period.value) {
                    store.dispatch(websiteDatesActionTypes.getWebsiteDatesfromLS, {
                        userId: userIdVal,
                        namespace: namespace,
                    });
                }
            },
            { immediate: true }
        );

        // Setup cleanup on component unmount
        setupUnmountCleanup();

        return {
            websites,
            websiteDatesModel,
            selectedWebsites,
            selectedPublishers,
            yieldPublishers,
            isSinglePublisherMode,
            tz,
            aabSiteIds,
            availableSlotNames,
            availableSlotNamesSelected,
            singlePublisherFilterData,
            multiPublisherFilterData,
            websitesReady,
        };
    },
};
</script>

<style lang="scss" scoped>
select#available_slots {
    appearance: auto;
    padding: 5px 10px;
    width: auto;
    box-sizing: border-box;
}
</style>
